/* eslint-disable no-param-reassign */

import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
  Col,
  UncontrolledTooltip,
} from 'reactstrap';
import { all } from 'axios';
import {
  capitalize,
  orderBy,
  isEqual,
  get,
} from 'lodash';

import SortableTree, {
  addNodeUnderParent,
  getVisibleNodeCount,
} from 'react-sortable-tree';
import 'react-sortable-tree/style.css';

import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useSlice from '~/utils/slice/hook';

import {
  referenceAdminInstance as refAdmin,
  toastSuccess,
  toastError,
} from '~/utils';

import NavigatoresqueSideNav from './NavigatoresqueSideNav';

const StyledSortableTree = styled(SortableTree)`
  .rst__moveHandle {
    border-right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow: none;
  }

  .rst__rowContents {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    box-shadow: none;
  }

  .newitem {
    .rst__rowContents {
      background-color: #d5f1ed;
    }
  }

  .rst__rowSearchFocus {
    outline: solid 3px #0080ff;
  }
`;

const saveItemOrder = (item, newOrder, newParent) => {
  if (!item.id) return Promise.resolve();

  if (item.order !== newOrder ||
      item.parentNavItemName !== (newParent && newParent.navItemName)) {
    return refAdmin.put(`/reference-nav-item/${item.id}`, {
      defaultSortOrder: newOrder,
      parentNavItemName: newParent ? newParent.navItemName : null,
    });
  }

  return Promise.resolve();
};

const saveItems = (items, parent = null) => {
  const savePromises = [];

  items.forEach((item, index) => {
    if (item.children) {
      savePromises.push(saveItems(item.children, item));
    }

    savePromises.push(saveItemOrder(item, index, parent));
  });

  return all(savePromises);
};

const NavItemSubTitle = ({ item, licenseTierLabels }) => {
  const [info, setInfo] = useState({});
  const { getInfo } = useSlice();
  const [licenseTier, setLicenseTier] = useState();
  const ltRef = useRef(null);
  const [featureFlag, setFeatureFlag] = useState([]);
  const ffRef = useRef(null);

  useEffect(() => {
    getInfo(item)
      .then(setInfo);

    setLicenseTier(licenseTierLabels[item.attributes.licenseTierLevel || 0] || licenseTierLabels[0]);

    setFeatureFlag(item.attributes.featureFlag);
  }, [item, licenseTierLabels]);

  return (<>
    <span className={`is-${info.type}`}>
      <span className="slice-label">
        [{capitalize(info.type)}{info.type !== 'system' && `: ${info.label}`}]
      </span>
    </span>

    {' '}

    <span className={`tier-${item.attributes.licenseTierLevel}`}>
      <span ref={ltRef} className="tier-label mx-1 px-2 rounded">{(licenseTier || '')[0]}</span>

      <UncontrolledTooltip
        target={ltRef}
        placement="bottom"
        delay={0}
      >
        {licenseTier}
      </UncontrolledTooltip>
    </span>

    {featureFlag && featureFlag.length > 0 && (<>
      {' '}

      <span ref={ffRef} className="text-info rounded">
        <FontAwesomeIcon icon="flag" />
      </span>

      <UncontrolledTooltip
        target={ffRef}
        placement="bottom"
        delay={0}
      >
        Feature flags: {featureFlag.join(', ')}
      </UncontrolledTooltip>
    </>)}
  </>);
};

NavItemSubTitle.propTypes = {
  item: PropTypes.shape().isRequired,
};

const navItemSearchMethod = ({ node, searchQuery: query }) => {
  if (!query) return false;
  const q = query.toLowerCase();
  return (node.navItemName && node.navItemName.toLowerCase().includes(q)) ||
    (node.navItemLabel && node.navItemLabel.toLowerCase().includes(q));
};

const SideNavPreview = ({
  items,
  editItem,
  reloadItems,
  isEditing,
  setIsEditing,
  licenseTierLabels,
  createSideNavItem,
}) => {
  const [groupedItems, setGroupedItems] = useState([]);
  const prevGroupedItemsRef = useRef([]);
  const [activeItems, setActiveItems] = useState([]);
  const prevActiveItemsRef = useRef([]);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const scrollRef = useRef(null);
  const hasAutoAddedItem = useRef(false);

  const {
    currentSlice,
  } = useSlice();

  const transformItemsForTree = (rawItems = []) => {
    const newItems = rawItems.map((i) => ({
      title: (<>
        {i.attributes.navItemLabel}
        {!i.attributes.isActive && <span className="text-danger"> (inactive)</span>}
      </>),
      subtitle: (<NavItemSubTitle item={i} licenseTierLabels={licenseTierLabels} />),
      expanded: i.expanded === undefined ? true : i.expanded,

      id: i.id,
      navItemName: i.attributes.navItemName,
      navItemLabel: i.attributes.navItemLabel,
      parentNavItemName: i.attributes.parentNavItemName,
      isActive: i.attributes.isActive,
      order: i.attributes.defaultSortOrder,
    }));

    const isOrphan = item => item.parentNavItemName &&
      !newItems.some(i => i.parentNavItemName === item.navItemName) &&
      newItems.some(i => i.navItemName === item.parentNavItemName);

    const hasOrphans = itemList => itemList.some(i => isOrphan(i));

    const moveChildToParent = (itemList, childIndex) => {
      const theChild = itemList[childIndex];
      const theParent = itemList.find(ii => ii.navItemName === theChild.parentNavItemName);
      if (!theParent.children) theParent.children = [];
      theParent.children.unshift(theChild);
      itemList.splice(childIndex, 1);
    };

    while (hasOrphans(newItems)) {
      for (let i = newItems.length - 1; i >= 0; i--) {
        if (isOrphan(newItems[i])) {
          moveChildToParent(newItems, i);
        }
      }
    }

    const orderItems = (itemList) => {
      itemList.forEach((item) => {
        if (item.children) {
          item.children = orderItems(item.children);
        }
      });

      return orderBy(itemList, ['order', 'text']);
    };

    return orderItems(newItems);
  };

  useEffect(() => {
    if (items) {
      const tree = transformItemsForTree(items);
      setGroupedItems(tree);
      prevGroupedItemsRef.current = tree;

      const actives = items.filter(i => i.attributes.isActive);
      const activeTree = transformItemsForTree(actives);
      setActiveItems(activeTree);
      prevActiveItemsRef.current = activeTree;
    }
  }, [items, licenseTierLabels]);

  const handleTreeChanged = (itemList, setItems, prevRef) => {
    const relevantAttrsChanged = (listA, listB) =>
      listA.some((item, index) => {
        if (!isEqual(item.children, listB[index].children) ||
            item.order !== listB[index].order ||
            item.parentNavItemName !== listB[index].parentNavItemName) {
          return true;
        }

        if (item.children && item.children.length &&
            listB[index].children && listB[index].children.length) {
          return relevantAttrsChanged(item.children, listB[index].children);
        }

        return false;
      });

    const changed = relevantAttrsChanged(itemList, prevRef.current);

    setItems(itemList);
    prevRef.current = itemList;

    if (changed) {
      saveItems(itemList)
        .then(reloadItems)
        .then(() => toastSuccess('Changes saved!'))
        .catch(e => toastError(`Error saving changes: ${e}`));
    }
  };

  const handleEditItem = (node) => {
    if (node.id) {
      editItem(items.find(i => i.id === node.id));
    } else {
      editItem({
        attributes: {
          navItemLabel: node.title,
          navItemName: node.title,
          route: node.route,
          parentNavItemName: node.parentNavItemName,
          defaultSortOrder: node.order,
          divisionUid: node.divisionUid,
          orgUid: node.orgUid,
          subSpecialtyUid: node.subSpecialtyUid,
          specialtyUid: node.specialtyUid,
          marketUid: node.marketUid,
          isActive: node.isActive,
        },
      });
    }
  };

  const handleAddItem = (extendedNode, itemParams = {}) => {
    const newNode = {
      title: itemParams.navItemName || 'New Item',
      route: itemParams.route,
      isActive: true,
      parentNavItemName: extendedNode && extendedNode.node && extendedNode.node.navItemName,
      order: extendedNode && extendedNode.node && extendedNode.node.children
        ? extendedNode.node.children.length : groupedItems.length - 1,

      marketUid: currentSlice.type === 'market' ? currentSlice.id : get(currentSlice, 'lineage.market'),
      orgUid: currentSlice.type === 'organization' ? currentSlice.id : get(currentSlice, 'lineage.organization'),
      divisionUid: currentSlice.type === 'division' ? currentSlice.id : get(currentSlice, 'lineage.division'),
      specialtyUid: currentSlice.type === 'specialty' ? currentSlice.id : get(currentSlice, 'lineage.specialty'),
      subSpecialtyUid: currentSlice.type === 'sub-specialty' ? currentSlice.id : get(currentSlice, 'lineage.sub-specialty'),
    };

    const currentTree = showDeactivated ? groupedItems : activeItems;

    const newTree = addNodeUnderParent({
      treeData: currentTree,
      newNode,
      parentKey: extendedNode && extendedNode.treeIndex,
      getNodeKey: ({ treeIndex }) => treeIndex,
    });

    if (showDeactivated) {
      setGroupedItems(newTree.treeData);
    } else {
      setActiveItems(newTree.treeData);
    }
    handleEditItem(newNode);

    if (!extendedNode) {
      setTimeout(() => {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  };

  useEffect(() => {
    if (scrollRef.current && createSideNavItem && !hasAutoAddedItem.current) {
      hasAutoAddedItem.current = true;
      setTimeout(() => {
        handleAddItem(undefined, createSideNavItem);
      }, 200);
    }
  }, [groupedItems.length]);

  const setExpandedRecursive = (nodes, expanded) =>
    nodes.map(node => ({
      ...node,
      expanded,
      ...(node.children ? { children: setExpandedRecursive(node.children, expanded) } : {}),
    }));

  const expandAll = (expanded = true) => {
    if (showDeactivated) {
      setGroupedItems(setExpandedRecursive(groupedItems, expanded));
    } else {
      setActiveItems(setExpandedRecursive(activeItems, expanded));
    }
  };

  return (
    <div>
      <Button
        color="primary"
        onClick={() => setIsEditing(!isEditing)}
        className="w-100 mb-3"
      >
        {isEditing ? (<>
          <FontAwesomeIcon icon="bars" className="mr-2" />Live View
        </>) : (<>
                        <FontAwesomeIcon icon="pencil-alt" className="mr-2" />Edit List
                              </>)}
      </Button>

      {isEditing ? (<>
        <Row>
          <Col className="d-flex">
            {(showDeactivated ? groupedItems : activeItems).some(i => !i.expanded) && (
              <Button
                color="secondary"
                outline
                className="w-100 mb-3"
                onClick={() => expandAll(true)}
              >
                <FontAwesomeIcon icon="caret-down" size="sm" className="mr-2" />expand all
              </Button>
            )}

            {(showDeactivated ? groupedItems : activeItems).some(i => i.expanded) && (
              <Button
                color="secondary"
                outline
                className="w-100 mb-3"
                onClick={() => expandAll(false)}
              >
                <FontAwesomeIcon icon="caret-up" size="sm" className="mr-2" />collapse all
              </Button>
            )}
          </Col>

          <Col className="d-flex">
            <Button
              color={showDeactivated ? 'warning' : 'secondary'}
              outline={!showDeactivated}
              className="w-100 mb-3"
              onClick={() => setShowDeactivated(!showDeactivated)}
            >
              <FontAwesomeIcon icon={showDeactivated ? 'eye' : 'eye-slash'} size="sm" className="mr-2" />
              {showDeactivated ? 'Showing' : 'Show'} Deactivated Items
            </Button>
          </Col>

          <Col>
            <Button type="submit" className="w-100 mb-3" onClick={() => handleAddItem()}>
              <FontAwesomeIcon icon="plus" size="sm" className="mr-2" />Add Item
            </Button>
          </Col>
        </Row>

        <InputGroup className="mb-3">
          <Input
            placeholder="Search by item name or label..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <InputGroupAddon addonType="append">
              <Button
                outline
                color="secondary"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <FontAwesomeIcon icon="times" />
              </Button>
            </InputGroupAddon>
          )}
        </InputGroup>

        {(showDeactivated ? groupedItems : activeItems).length ? (
          <div style={{
            height: getVisibleNodeCount({
              treeData: showDeactivated ? groupedItems : activeItems,
            }) * 70,
          }}
          >
            <StyledSortableTree
              treeData={showDeactivated ? groupedItems : activeItems}
              onChange={il => handleTreeChanged(
                il,
                showDeactivated ? setGroupedItems : setActiveItems,
                showDeactivated ? prevGroupedItemsRef : prevActiveItemsRef,
              )}
              searchQuery={searchQuery || null}
              searchMethod={navItemSearchMethod}
              generateNodeProps={extendedNode => ({
                buttons: [
                  <FontAwesomeIcon
                    icon="edit"
                    onClick={() => handleEditItem(extendedNode.node)}
                    className="mx-1"
                    style={{ cursor: 'pointer' }}
                    title="Edit item"
                  />,
                  <FontAwesomeIcon
                    icon="plus-circle"
                    onClick={() => handleAddItem(extendedNode)}
                    className="mx-1 text-success"
                    style={{ cursor: 'pointer' }}
                    title="Add child item"
                  />,
                ],
                className: (() => {
                  if (!extendedNode.node.id) return 'newitem';
                  if (!extendedNode.node.isActive) return 'inactive';
                  return '';
                })(),
              })}
            />

            <div ref={scrollRef} />
          </div>
        ) : (
          <p>No items configured at this context level for this module</p>
        )}
      </>) : (
                      <NavigatoresqueSideNav items={activeItems} />
      )}
    </div>
  );
};

SideNavPreview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  editItem: PropTypes.func.isRequired,
  reloadItems: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  licenseTierLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  createSideNavItem: PropTypes.shape(),
};

SideNavPreview.defaultProps = {
  createSideNavItem: undefined,
};

export default SideNavPreview;
